import LegalLayout from "./LegalLayout";

export default function Terms() {
  return (
    <LegalLayout
      eyebrow="Legal"
      title="Terms &amp; Conditions"
      subtitle="Last updated: 01 September 2026"
    >
      <p>
        These Terms &amp; Conditions (&ldquo;Terms&rdquo;) govern your access to
        and use of the Matcha Story mobile application, website
        (matchastory.my) and related services (the &ldquo;Service&rdquo;),
        operated by <strong>Progressive Synergy Sdn Bhd</strong>
        (&ldquo;Company&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo; or
        &ldquo;our&rdquo;). By using the Service you agree to be bound by these
        Terms.
      </p>

      <Section n="1" title="Eligibility">
        <p>You must be at least 13 years old (or the minimum age required by the laws of your jurisdiction) to use the Service. By using the Service you represent that you meet this requirement.</p>
      </Section>

      <Section n="2" title="Accounts">
        <p>You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account. Notify us immediately of any unauthorised use at <a href="mailto:hello@matchastory.my" className="text-matcha-dark underline underline-offset-4">hello@matchastory.my</a>.</p>
        <p>We may suspend or terminate accounts that violate these Terms, engage in fraud, abuse or unlawful activity, or that pose a risk to other users or to the Service.</p>
      </Section>

      <Section n="3" title="Orders, Payments &amp; Refunds">
        <p>Products displayed in the Service are an invitation to treat and not a binding offer. We may accept or decline any order at our discretion.</p>
        <p>Prices are listed in Malaysian Ringgit (MYR) and are inclusive of any applicable taxes unless stated otherwise. Payments are processed by our payment gateway partner.</p>
        <p>Refunds and exchanges follow our in-store service policy. Perishable items generally are not refundable except where defective, incorrectly prepared, or where required by law.</p>
      </Section>

      <Section n="4" title="Loyalty, Points &amp; Wallet">
        <p>Points, stamps, vouchers and wallet balances have no monetary value outside the Service, are non-transferable, non-refundable for cash, and may expire in accordance with the programme terms disclosed in the Application.</p>
        <p>We may adjust, revoke or expire loyalty balances obtained through error, fraud or breach of these Terms.</p>
      </Section>

      <Section n="5" title="Acceptable Use">
        <p>You agree not to:</p>
        <ul>
          <li>use the Service for any unlawful, harmful or fraudulent purpose;</li>
          <li>interfere with, disrupt or attempt to gain unauthorised access to the Service or any related systems;</li>
          <li>upload or transmit any malicious code or content that infringes intellectual-property or privacy rights;</li>
          <li>reverse-engineer or copy any part of the Service except as expressly permitted by law.</li>
        </ul>
      </Section>

      <Section n="6" title="Intellectual Property">
        <p>All trademarks, logos, product names, photography, recipes, written content and software associated with Matcha Story are the property of Progressive Synergy Sdn Bhd or its licensors, and are protected by applicable intellectual-property laws. You are granted a limited, non-exclusive, non-transferable licence to use the Service solely for its intended purpose.</p>
      </Section>

      <Section n="7" title="Third-Party Services">
        <p>The Service integrates with third-party providers (payment gateways, delivery partners, cloud infrastructure, analytics, app stores). Their services are governed by their own terms and privacy policies, which we do not control.</p>
      </Section>

      <Section n="8" title="Disclaimer of Warranties">
        <p>The Service is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo; without warranties of any kind, whether express or implied, including warranties of merchantability, fitness for a particular purpose, or non-infringement. We do not guarantee that the Service will be uninterrupted, error-free, or secure.</p>
      </Section>

      <Section n="9" title="Limitation of Liability">
        <p>To the maximum extent permitted by law, Progressive Synergy Sdn Bhd, its directors, employees and affiliates shall not be liable for any indirect, incidental, special, consequential or punitive damages arising out of or relating to your use of the Service.</p>
        <p>Our aggregate liability for any claim arising out of or relating to these Terms or the Service shall not exceed the greater of RM 100 or the amount you paid to us in the six months preceding the claim.</p>
      </Section>

      <Section n="10" title="Indemnity">
        <p>You agree to indemnify and hold us harmless from any claim, demand, loss or expense (including reasonable legal fees) arising from your breach of these Terms, misuse of the Service, or violation of any law or third-party right.</p>
      </Section>

      <Section n="11" title="Termination">
        <p>You may stop using the Service at any time and may delete your account via the <a href="/delete-account" className="text-matcha-dark underline underline-offset-4">Account &amp; Data Deletion</a> page or in-app settings. We may suspend or terminate your access at our discretion for breach of these Terms or for operational, legal or security reasons.</p>
      </Section>

      <Section n="12" title="Changes to These Terms">
        <p>We may update these Terms from time to time. Where appropriate, we will notify you of material changes through the Service. Your continued use of the Service after the updated Terms take effect constitutes acceptance of the changes, to the extent permitted by law.</p>
      </Section>

      <Section n="13" title="Governing Law">
        <p>These Terms are governed by the laws of Malaysia. Any dispute arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of Malaysia, without prejudice to any mandatory consumer-protection rights available to you under applicable law.</p>
      </Section>

      <Section n="14" title="Contact Us">
        <p>
          <strong>Progressive Synergy Sdn Bhd</strong><br />
          Brand: Matcha Story<br />
          Email: <a href="mailto:hello@matchastory.my" className="text-matcha-dark underline underline-offset-4">hello@matchastory.my</a>
        </p>
      </Section>
    </LegalLayout>
  );
}

function Section({ n, title, children }) {
  return (
    <section className="pt-4" data-testid={`terms-section-${n}`}>
      <h2 className="overline text-ink mb-3">
        <span className="text-matcha-dark mr-2">{n}.</span>
        {title}
      </h2>
      <div className="space-y-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5">
        {children}
      </div>
    </section>
  );
}
